// ============================================================================
// ENUMS
// ============================================================================

export enum KnowledgeBaseCategory {
  Snapshot = 'Snapshot',
  IdealCustomerProfile = 'Ideal Customer Profile',
  Content = 'Content',
  ToneOfVoice = 'Tone of Voice',
  CoreNarrativeAndPillars = 'Core Narrative and Pillars',
  BrandGuidelines = 'Brand Guidelines',
  VisualNarrative = 'Visual Narrative',
  PainPoints = 'Pain Points',
  Offers = 'Offers',
  Keywords = 'Keywords',
  MissionVisionValues = 'Mission, Vision, and Values',
  Objections = 'Objections',
  CaseStudiesAndTestimonials = 'Case Studies and Testimonials',
  DefaultMarketingLanguage = 'Default Marketing Language',
  Faqs = 'FAQs',
  Compliance = 'Compliance',
}

export enum KnowledgeBaseLevel {
  Company = 'Company',
  Workspace = 'Workspace',
  Campaign = 'Campaign',
}

// Use a const object instead of a TypeScript enum to avoid circular-import
// timing issues that cause the enum to resolve as `undefined` at runtime
// in Vite/ESM builds (observed as: Cannot read properties of undefined (reading 'USEnglish')).
export const MarketingLanguage = {
  USEnglish: 'USEnglish',
  UKEnglish: 'UKEnglish',
} as const
export type MarketingLanguage = typeof MarketingLanguage[keyof typeof MarketingLanguage]

// ============================================================================
// CATEGORY CONFIG (from server metadata endpoint)
// ============================================================================

export interface KnowledgeBaseCategoryConfigDto {
  Category: string
  DisplayName: string
  Description: string
  SupportsText: boolean
  SupportsUpload: boolean
  SupportsLinks: boolean
  SupportsDropdown: boolean
  AiPopulatable: boolean
  CampaignOverridable: boolean
}

// ============================================================================
// COLOR PALETTE
// ============================================================================

export interface KnowledgeBaseColorPaletteDto {
  Primary?: string | null
  Secondary?: string | null
  Accent?: string | null
  Background?: string | null
  Text?: string | null
}

// ============================================================================
// ASSET TYPES (for typed file uploads on Brand Guidelines)
// ============================================================================

export type KnowledgeBaseAssetType = 'Logo' | 'Icon' | 'Banner'

// ============================================================================
// FILE DTOs
// ============================================================================

export interface KnowledgeBaseFileViewDto {
  FileId: string
  FileName: string
  ContentType: string
  FileSizeBytes: number
  DownloadUrl: string
  UploadedAt: string
  UploadedByName?: string
  AssetType?: KnowledgeBaseAssetType
}

// ============================================================================
// ENTRY VIEW DTO (response from GET single category)
// ============================================================================

export interface KnowledgeBaseEntryViewDto {
  Id: string
  ClientCompanyId: string
  SettingsLevel: string
  TargetId: string
  Category: string
  CategoryDisplayName: string

  WorkspaceId?: string
  CampaignId?: string

  InheritFromParent: boolean

  TextContent?: string
  DropdownValue?: string
  ColorPalette?: KnowledgeBaseColorPaletteDto | null
  Links: string[]
  Files: KnowledgeBaseFileViewDto[]

  IsAiGenerated: boolean
  AiGeneratedAt?: string
  AiSourceUrl?: string
  IsUserReviewed: boolean

  EffectiveLevel?: string
  IsInherited: boolean

  CreatedByName?: string
  CreatedDate?: string
  UpdatedByName?: string
  UpdatedDate?: string

  CategoryConfig: KnowledgeBaseCategoryConfigDto
}

// ============================================================================
// SUMMARY DTOs (response from GET all categories)
// ============================================================================

export interface KnowledgeBaseCategoryStatusDto {
  Category: string
  CategoryDisplayName: string
  IsPopulated: boolean
  IsInherited: boolean
  EffectiveLevel: string
  IsAiGenerated: boolean
  AiGeneratedAt?: string
  IsUserReviewed: boolean
  HasFiles: boolean
  FileCount: number
  Config: KnowledgeBaseCategoryConfigDto
}

export interface KnowledgeBaseSummaryViewDto {
  ClientCompanyId: string
  WorkspaceId?: string
  CampaignId?: string
  SettingsLevel: string
  Categories: KnowledgeBaseCategoryStatusDto[]
}

// ============================================================================
// REQUEST DTOs
// ============================================================================

export interface KnowledgeBaseEntryUpsertDto {
  ClientCompanyId: string
  WorkspaceId?: string
  CampaignId?: string
  InheritFromParent: boolean
  TextContent?: string
  DropdownValue?: string
  ColorPalette?: KnowledgeBaseColorPaletteDto | null
  Links?: string[]
}

export interface KnowledgeBaseAiPopulateDto {
  WebsiteUrl?: string
  OverwriteExisting: boolean
  SubpageUrls?: string[]
}

export interface KnowledgeBaseFileUploadDto {
  FileName: string
  ContentType: string
  FileSizeBytes: number
}

// ============================================================================
// AI POPULATE RESULT DTOs
// ============================================================================

export interface KnowledgeBaseAiCategoryResultDto {
  Category: string
  Status: string
  Reason?: string
}

export interface KnowledgeBaseAiPopulateResultDto {
  TotalCategories: number
  PopulatedCount: number
  SkippedCount: number
  FailedCount: number
  ScrapedUrls: string[]
  Results: KnowledgeBaseAiCategoryResultDto[]
}
